import { createCustomAction } from 'typesafe-actions';

import { ValidationErrorData } from '../../typings/common';
import { PcodeAds } from '../../typings/pcode';
import {
    ApiCropData,
    ApiGalleryItem,
    Bundle,
    CanvasApiValidationErrors,
    CreativeCropApplyResponse
} from '../common/requestsTypings';
import { Page } from '../common/typings';
import { CreativeImageCropSize } from '../CreativeImageCropModal/typings';
import { CreativeOptionsData } from '../CreativeOptions/typings';
import { GalleryCollectionItem } from '../GalleryPane/typings';

import { CommonCreativeItem, EditingFileInfo } from './typings';

export const openConstructor = createCustomAction(
    '@canvasAddCreative/OPEN',
    type => (from: Page, presetId?: string, id?: string, ideaId?: string, isAdaptive?: boolean) => ({
        type,
        presetId,
        from,
        id,
        ideaId,
        isAdaptive
    })
);

export const initConstructor = createCustomAction('@canvasAddCreative/INIT', type => {
    return (from: Page, presetId?: string, id?: string) => ({ type, id, presetId, from });
});

export const reset = createCustomAction('@canvasAddCreative/RESET');
export const closeConstructor = createCustomAction('@canvasAddCreative/CLOSE');
export const returnSelect = createCustomAction('@canvasAddCreative/RETURN_SELECT', type => (page?: Page) => ({
    type,
    page
}));

export const showError = createCustomAction('@canvasAddCreative/SHOW_ERROR', type => (error: ValidationErrorData) => ({
    type,
    error
}));

export const optionChange = createCustomAction(
    '@canvasAddCreative/OPTION_CHANGE',
    type => (groupName: string, name: string, value: string) => ({
        type,
        groupName,
        name,
        value
    })
);

export const toggleOptionGroupVisibility = createCustomAction(
    '@canvasAddCreative/TOGGLE_OPTION_GROUP_VISIBILITY',
    type => (groupName: string) => ({ type, groupName })
);

export const saveName = createCustomAction('@canvasAddCreative/SAVE_NAME', type => (name: string) => ({ type, name }));

export const showOptionsErrors = createCustomAction(
    '@canvasAddCreative/SHOW_OPTIONS_ERRORS',
    type => (errors: CanvasApiValidationErrors) => ({ type, errors })
);

export const showOptionErrors = createCustomAction(
    '@canvasAddCreative/SHOW_OPTION_ERRORS',
    type => (groupName: string, name: string, errors: ValidationErrorData[]) => ({ type, groupName, name, errors })
);

export const clearOptionErrors = createCustomAction(
    '@canvasAddCreative/CLEAR_OPTION_ERRORS',
    type => (groupName: string, name: string) => ({ type, groupName, name })
);

export const setCreativeOptions = createCustomAction(
    '@canvasAddCreative/SET_CREATIVE_OPTIONS',
    type => (
        creativeOptions: CreativeOptionsData,
        bundle?: {
            name: string;
        },
        presetId?: string,
        isAudio?: boolean,
        id?: string
    ) => ({
        type,
        creativeOptions,
        bundle,
        presetId,
        isAudio,
        id
    })
);

export const clearErrors = createCustomAction('@canvasAddCreative/CLEAR_ERRORS');

export const changeOptionEditable = createCustomAction(
    '@canvasAddCreative/CHANGE_OPTION_EDITABLE',
    type => (groupName: string, name: string, value: boolean) => ({ type, groupName, name, value })
);

export const changeAddButtonAvailable = createCustomAction(
    '@canvasAddCreative/CHANGE_ADD_BUTTON_AVAILABLE',
    type => (value: boolean) => ({ type, value })
);

export const hideLoading = createCustomAction('@canvasAddCreative/HIDE_LOADING');

export const hideSaving = createCustomAction('@canvasAddCreative/HIDE_SAVING', type => (createPressed?: boolean) => ({
    type,
    createPressed
}));

/* actions для видео креатива */

export const initVideoData = createCustomAction(
    '@canvasAddCreative/INIT_VIDEO_DATA',
    type => (videoPreviewData?: PcodeAds) => ({
        type,
        videoPreviewData
    })
);

export const fetchVideoVast = createCustomAction('@canvasAddCreative/FETCH_VIDEO_VAST');

export const setPcodePreviewData = createCustomAction(
    '@canvasAddCreative/SET_PCODE_PREVIEW_DATA',
    type => (vast: string) => ({
        type,
        vast
    })
);

export const createVideoCreative = createCustomAction(
    '@canvasAddCreative/CREATE_VIDEO_CREATIVE',
    type => (creativesSelect: (ids: number[]) => void) => ({ type, creativesSelect })
);

export const shouldCreateVideoCreative = createCustomAction(
    '@canvasAddCreative/SHOULD_CREATE_VIDEO_CREATIVE',
    type => () => ({ type })
);

export const closeLongUploadPopup = createCustomAction('@canvasAddCreative/CLOSE_LONG_UPLOAD_POPUP');

export const openLongUploadPopup = createCustomAction('@canvasAddCreative/OPEN_LONG_UPLOAD_POPUP');

export const showPreviewLoading = createCustomAction('@canvasAddCreative/SHOW_PREVIEW_LOADING');

export const hidePreviewLoading = createCustomAction('@canvasAddCreative/HIDE_PREVIEW_LOADING');

export const changeOptionsOverlayColor = createCustomAction(
    '@canvasAddCreative/CHANGE_OPTIONS_OVERLAY_COLOR',
    type => (overlayColor: string) => ({
        type,
        overlayColor
    })
);

export const adEnd = createCustomAction('@canvasAddCreative/AD_END');

/* actions для common и adaptive креативов */

export const initCommonCreative = createCustomAction('@canvasAddCreative/INIT_COMMON_CREATIVE');
export const initAdaptiveCreative = createCustomAction('@canvasAddCreative/INIT_ADAPTIVE_CREATIVE');

export const initCommonCreativeSuccess = createCustomAction(
    '@canvasAddCreative/INIT_COMMON_CREATIVE_SUCCESS',
    type => (result: {
        options: CreativeOptionsData;
        items: CommonCreativeItem[];
        bundle: Bundle;
        presetId?: string;
        isIdea?: boolean;
        batchId?: string;
    }) => ({
        type,
        result
    })
);

export const initAdaptiveCreativeSuccess = createCustomAction(
    '@canvasAddCreative/INIT_ADAPTIVE_CREATIVE_SUCCESS',
    type => (result: {
        options: CreativeOptionsData;
        item: CommonCreativeItem;
        bundle: Bundle;
        presetId?: string;
        isIdea?: boolean;
        batchId?: string;
    }) => ({
        type,
        result
    })
);

export const createGraphicCreative = createCustomAction('@canvasAddCreative/CREATE_GRAPHIC_CREATIVE');
export const cropModalClose = createCustomAction('@canvasAddCreative/CROP_MODAL_CLOSE');

export const showPreviewsModal = createCustomAction(
    '@canvasAddCreative/SHOW_PREVIEWS_MODAL',
    type => (updatePreviews?: boolean) => ({ type, updatePreviews })
);

export const cancelPreviewsModal = createCustomAction('@canvasAddCreative/CANCEL_PREVIEWS_MODAL');
export const setSmartCenter = createCustomAction('@canvasAddCreative/SET_SMART_CENTER');
export const closePreviewsModal = createCustomAction('@canvasAddCreative/CLOSE_PREVIEWS_MODAL');
export const resetPreviewsModal = createCustomAction('@canvasAddCreative/RESET_PREVIEWS_MODAL');

export const submitPreviewsModal = createCustomAction(
    '@canvasAddCreative/SUBMIT_PREVIEWS_MODAL',
    type => (continueEdit?: boolean) => ({ type, continueEdit })
);
export const applyPreviewsSelect = createCustomAction('@canvasAddCreative/APPLY_PREVIEWS_SELECT');
export const applyFileChanges = createCustomAction('@canvasAddCreative/APPLY_FILE_CHANGES');

export const togglePreview = createCustomAction(
    '@canvasAddCreative/TOGGLE_PREVIEW',
    type => (ids: number[], val: boolean) => ({ type, ids, val })
);

export const crop = createCustomAction('@canvasAddCreative/CROP', type => (id: number) => ({ type, id }));

export const changeSize = createCustomAction('@canvasAddCreative/CHANGE_SIZE', type => (value: number) => ({
    type,
    value
}));

export const checkSize = createCustomAction('@canvasAddCreative/CHECK_SIZE', type => (value: number) => ({
    type,
    value
}));

export const applyGraphicCreativeFile = createCustomAction(
    '@canvasAddCreative/APPLY_GRAPHIC_CREATIVE_FILE',
    type => (editorId: string, item: ApiGalleryItem | GalleryCollectionItem, groupName: string) => ({
        type,
        editorId,
        item,
        groupName
    })
);

export const applyCropSuccess = createCustomAction(
    '@canvasAddCreative/APPLY_CROP_SUCCESS',
    type => (response: CreativeCropApplyResponse, editedSizes: CreativeImageCropSize[]) => ({
        type,
        response,
        editedSizes
    })
);

export const applySmartCenterSuccess = createCustomAction(
    '@canvasAddCreative/APPLY_SMART_CENTER_SUCCESS',
    type => (response: CreativeCropApplyResponse, smartCenters: ApiCropData[]) => ({
        type,
        response,
        smartCenters
    })
);

export const setEditingFileInfo = createCustomAction(
    '@canvasAddCreative/SET_EDITING_FILE_INFO',
    type => (editingFileInfo: EditingFileInfo) => ({
        type,
        editingFileInfo
    })
);

/* actions для Overlay */

export const initOverlayData = createCustomAction('@canvasAddCreative/INIT_OVERLAY_DATA');

export const fetchOverlayVast = createCustomAction('@canvasAddCreative/FETCH_OVERLAY_VAST');

export const createOverlay = createCustomAction(
    '@canvasAddCreative/CREATE_OVERLAY',
    type => (creativesSelect: (ids: number[]) => void) => ({ type, creativesSelect })
);

export type OptionChangeAction = ReturnType<typeof optionChange>;
export type InitVideoDataAction = ReturnType<typeof initVideoData>;
export type InitOverlayDataAction = ReturnType<typeof initOverlayData>;
export type ToggleOptionGroupVisibilityAction = ReturnType<typeof toggleOptionGroupVisibility>;
export type CreateVideoCreativeAction = ReturnType<typeof createVideoCreative>;
export type CreateOverlayAction = ReturnType<typeof createOverlay>;
export type FetchVideoVastAction = ReturnType<typeof fetchVideoVast>;
export type FetchOverlayVastAction = ReturnType<typeof fetchOverlayVast>;
export type ApplyCommonFileAction = ReturnType<typeof applyGraphicCreativeFile>;
export type ApplyCropSuccessAction = ReturnType<typeof applyCropSuccess>;
export type ApplySmartCenterSuccessAction = ReturnType<typeof applySmartCenterSuccess>;
export type CropAction = ReturnType<typeof crop>;
export type SubmitPreviewsModalAction = ReturnType<typeof submitPreviewsModal>;
export type OpenAction = ReturnType<typeof openConstructor>;
export type ReturnSelectAction = ReturnType<typeof returnSelect>;

export type AddCreativeAction =
    | OptionChangeAction
    | InitVideoDataAction
    | InitOverlayDataAction
    | ToggleOptionGroupVisibilityAction
    | CreateVideoCreativeAction
    | CreateOverlayAction
    | FetchVideoVastAction
    | FetchOverlayVastAction
    | ApplyCommonFileAction
    | CropAction
    | ApplyCropSuccessAction
    | ApplySmartCenterSuccessAction
    | OpenAction
    | ReturnType<typeof adEnd>
    | ReturnType<typeof closeConstructor>
    | ReturnSelectAction
    | ReturnType<typeof initConstructor>
    | ReturnType<typeof shouldCreateVideoCreative>
    | ReturnType<typeof changeOptionEditable>
    | ReturnType<typeof saveName>
    | ReturnType<typeof showOptionErrors>
    | ReturnType<typeof clearOptionErrors>
    | ReturnType<typeof reset>
    | ReturnType<typeof setCreativeOptions>
    | ReturnType<typeof clearErrors>
    | ReturnType<typeof showError>
    | ReturnType<typeof hideLoading>
    | ReturnType<typeof changeAddButtonAvailable>
    | ReturnType<typeof hideSaving>
    | ReturnType<typeof setPcodePreviewData>
    | ReturnType<typeof openLongUploadPopup>
    | ReturnType<typeof closeLongUploadPopup>
    | ReturnType<typeof showPreviewLoading>
    | ReturnType<typeof hidePreviewLoading>
    | ReturnType<typeof changeOptionsOverlayColor>
    | ReturnType<typeof showOptionsErrors>
    | SubmitPreviewsModalAction
    | ReturnType<typeof cancelPreviewsModal>
    | ReturnType<typeof cropModalClose>
    | ReturnType<typeof setSmartCenter>
    | ReturnType<typeof togglePreview>
    | ReturnType<typeof changeSize>
    | ReturnType<typeof checkSize>
    | ReturnType<typeof initCommonCreative>
    | ReturnType<typeof applyPreviewsSelect>
    | ReturnType<typeof applyFileChanges>
    | ReturnType<typeof initCommonCreativeSuccess>
    | ReturnType<typeof initAdaptiveCreativeSuccess>
    | ReturnType<typeof initAdaptiveCreative>
    | ReturnType<typeof createGraphicCreative>
    | ReturnType<typeof closePreviewsModal>
    | ReturnType<typeof resetPreviewsModal>
    | ReturnType<typeof setEditingFileInfo>
    | ReturnType<typeof showPreviewsModal>;
